import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, api } from "../context/AuthContext";
import { ArrowRight, Sparkles } from "lucide-react";

const FLIP_PERIOD = 6000;
const ARCH_LOOP_S = 90;

const SCRAMBLE_GLYPHS = "!<>-_\\/[]{}=+*^?#01";

// Matrix typewriter: characters cycle through glyphs, then lock in left to right.
function ScrambleWord({ a, b, showSecond, gradient }) {
  const target = showSecond ? b : a;
  const maxLen = Math.max(a.length, b.length);
  const [chars, setChars] = useState(() => a.split(""));
  const mounted = useRef(false);

  useEffect(() => {
    const final = target.split("");
    while (final.length < maxLen) final.push("");
    if (!mounted.current) {
      mounted.current = true;
      setChars(final);
      return;
    }
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setChars(final);
      return;
    }
    let frame = 0;
    const totalFrames = maxLen * 2 + 8;
    const id = setInterval(() => {
      frame += 1;
      const revealed = Math.floor((frame / totalFrames) * (maxLen + 1));
      setChars(
        final.map((ch, i) => {
          if (ch === "") return "";
          if (i < revealed) return ch;
          return SCRAMBLE_GLYPHS[Math.floor(Math.random() * SCRAMBLE_GLYPHS.length)];
        })
      );
      if (frame >= totalFrames) {
        clearInterval(id);
        setChars(final);
      }
    }, 45);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSecond]);

  const gradStyle = (i, n) =>
    gradient
      ? {
          backgroundImage: "linear-gradient(to right, #0A84FF, #a855f7)",
          backgroundSize: `${n * 100}% 100%`,
          backgroundPosition: `${n > 1 ? (i / (n - 1)) * 100 : 0}% 0`,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }
      : undefined;

  return (
    <span aria-label={target}>
      {chars.map((ch, i) => (
        <span key={i} aria-hidden="true" style={gradStyle(i, chars.length)}>
          {ch === "" ? " " : ch}
        </span>
      ))}
    </span>
  );
}

// Coverflow arch: thumbnails travel along a curved 3D track (raised center,
// tilted edges), positioned with pure math in one rAF loop — no layout reads.
function ArchCarousel({ covers }) {
  const boxRef = useRef(null);
  const imgsRef = useRef([]);
  const reduceMotion = useRef(
    typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    if (!covers.length) return;
    const box = boxRef.current;
    let raf = 0;
    let W = 0;
    let total = 0;
    const measure = () => {
      const md = window.innerWidth >= 768;
      const w = md ? 168 : 120;
      W = w - 36; // negative gap: cards overlap like coverflow
      total = covers.length * W;
    };
    measure();
    window.addEventListener("resize", measure);

    const place = (off) => {
      const cw = box.clientWidth || window.innerWidth;
      const shift = (cw - total) / 2;
      const els = imgsRef.current;
      for (let k = 0; k < els.length; k++) {
        const el = els[k];
        if (!el) continue;
        const i = k % covers.length;
        let x = (i * W - off) % total;
        if (x < 0) x += total;
        const left = x + shift;
        const cx = left + W / 2;
        const dx = cx - cw / 2;
        const t = Math.max(-1.15, Math.min(1.15, dx / (cw / 2)));
        const at = Math.abs(t);
        const y = -70 * (1 - t * t);
        const ry = -22 * t;
        const s = 1 - 0.24 * at;
        el.style.left = `${left.toFixed(1)}px`;
        el.style.transform =
          `translate(-50%,-50%) translateY(${y.toFixed(1)}px) rotateY(${ry.toFixed(2)}deg) scale(${s.toFixed(3)})`;
        el.style.opacity = "1";
        el.style.zIndex = String(100 - Math.round(at * 50));
      }
    };

    if (reduceMotion.current) {
      place(0);
      return () => window.removeEventListener("resize", measure);
    }
    let off = 0;
    let last = -1;
    const tick = (t) => {
      if (last < 0) last = t;
      const dt = Math.min(0.05, (t - last) / 1000);
      last = t;
      off = (off + (dt * total) / ARCH_LOOP_S) % total;
      place(off);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [covers]);

  return (
    <div ref={boxRef} className="relative h-[300px] md:h-[340px] overflow-visible" style={{ perspective: "1200px", transformStyle: "preserve-3d" }}>
      {covers.map((c, i) => (
        <img
          key={`${i}-${c}`}
          ref={(el) => {
            imgsRef.current[i] = el;
          }}
          src={c}
          alt=""
          loading="eager"
          decoding="async"
          draggable={false}
          className="absolute left-0 top-1/2 w-[120px] md:w-[168px] aspect-[3/4] rounded-2xl object-cover will-change-transform"
          style={{ WebkitTransform: "translateZ(0)" }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const { user, login } = useAuth();
  const [covers, setCovers] = useState([]);
  const [alt, setAlt] = useState(false);

  useEffect(() => {
    api.get("/games").then((r) => setCovers((r.data.games || []).map((g) => g.cover).filter(Boolean))).catch(() => {});
  }, []);

  useEffect(() => {
    const id = setInterval(() => setAlt((v) => !v), FLIP_PERIOD);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative pt-40 pb-28 px-6 md:px-10 overflow-hidden">
      <div className="max-w-5xl mx-auto text-center relative z-10">
        <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-[12px] text-white/70">
          <Sparkles className="w-3.5 h-3.5 text-[#0A84FF]" /> New drop · Money Hawk 3D now live on Stake
        </div>
        <h1 className="relative mt-8 text-[52px] sm:text-[72px] md:text-[88px] font-black tracking-[-0.045em] leading-[0.92] text-white">
          {covers.length > 0 && (
            <div aria-hidden className="absolute left-1/2 top-1/2 w-screen pointer-events-none select-none" style={{ transform: "translate(-50%, calc(-50% + 360px))" }}>
              <div className="relative overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_18%,black_82%,transparent)]">
                <ArchCarousel covers={covers} />
                <div className="absolute inset-0 bg-[#08090c]/70 pointer-events-none" />
                <div className="absolute inset-y-0 left-0 w-40 bg-gradient-to-r from-[#08090c] to-transparent" />
                <div className="absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-[#08090c] to-transparent" />
              </div>
            </div>
          )}
          <ScrambleWord a="Slots" b="Games" showSecond={alt} gradient /> <span className="text-white">worth</span><br /><ScrambleWord a="spinning." b="playing." showSecond={alt} gradient />
        </h1>
        <p className="relative mt-6 mx-auto max-w-xl text-[17px] leading-relaxed text-white/85">
          Gumble is an independent studio building math-first slot titles. Provably fair, verified by Stake Engine, tuned for volatile sessions you'll remember.
        </p>
        <div className="relative mt-10 flex items-center justify-center gap-3 flex-wrap">
          <a href="#games" className="inline-flex items-center gap-2 pl-6 pr-5 py-3 rounded-full bg-white text-black text-[14px] font-semibold hover:bg-white active:scale-[0.98] transition">
            Explore games <ArrowRight className="w-4 h-4" />
          </a>
          {user ? (
            <Link to="/challenges" className="inline-flex items-center gap-2 pl-6 pr-5 py-3 rounded-full bg-[#20232b] border border-white/10 text-[14px] text-white hover:bg-[#2a2e38] transition-colors">
              Go to challenges <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <button onClick={login} className="relative overflow-hidden inline-flex items-center gap-2 pl-6 pr-5 py-3 rounded-full bg-[#20232b] border border-white/10 text-[14px] text-white hover:bg-[#2a2e38] transition-colors">
              <span aria-hidden className="anim-sheen-traverse pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              Sign in with Discord
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
